import { InputAdornment, TextField } from "@vapor/react-material";
import { getISODateAndMonth, getISOFromDateAndMonth } from "../../../../utils/functions/date/convert";
import { RadioSubValues } from "@onefront/react-workspace-components/RadioSub/RadioSub";

const subcomponentCashAccounting = t => {
    return {
        k: "cashAccounting",
        component: "radio",
        radioGroupAlign: "vertical",
        fullWidth: true,
        style: { width: "100%" },
        options: [
            { value: t("general.no"), id: false },
            { value: t("general.yes"), id: true }
        ]
    };
};

export const specialVatRegimeOptions = (t, isEditing) => {
    return [
        {
            value: "Nessun regime speciale",
            id: "NO_SPECIAL_REGIME",
            subcomponent: {
                ...subcomponentCashAccounting(t),
                labelWithTooltip: {
                    label: t("business.fields.cashAccounting.label"),
                    tooltip: {
                        title: t("business.fields.cashAccounting.tips.title"),
                        message: t("business.fields.cashAccounting.tips.description"),
                        placement: "bottom"
                    },
                    showTooltip: isEditing
                }
            }
        },
        { value: "Agenzia viaggi", id: "TRAVEL_AGENCY" },
        { value: "Agricoltura speciale", id: "SPECIAL_SCHEME_OF_AGRICOLTURE" },
        { value: "Agricoltura esonero", id: "AGRICOLTURE_EXEMPTION" },
        {
            value: "Autotrasporto conto terzi",
            id: "TRUCK_THIRD_PARTIES",
            subcomponent: {
                ...subcomponentCashAccounting(t),
                labelWithTooltip: {
                    label: t("business.fields.cashAccounting.label"),
                    tooltip: {
                        title: t("business.fields.cashAccounting.tips.title"),
                        message: t("business.fields.cashAccounting.tips.description"),
                        placement: "bottom"
                    },
                    showTooltip: isEditing
                }
            }
        },
        { value: "Editoria", id: "PUBLISHING" },
        { value: "Margine analitico", id: "ANALYTICAL_MARGIN" },
        { value: "Margine globale", id: "GLOBAL_MARGIN" },
        {
            value: "Margine forfettario",
            id: "FLAT_RATE_MARGIN",
            subcomponent: {
                k: "flatTaxAccountingRate",
                label: t("business.fields.flatTaxAccountingRate"),
                component: "textfield",
                componentType: "number",
                additionalProps: {
                    InputProps: {
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        style: { textAlign: "left", width: "5.5rem" },
                        inputProps: {
                            style: { textAlign: "left", width: "2.5rem" },
                            onChange: e => {
                                const value = e.target.value;
                                if (value && !/^\d+$/.test(value)) {
                                    e.target.value = value.slice(0, -1);
                                }
                                e.target.value = e.target.value.replace(/\D/g, "");
                                if (e.target.value > 100) {
                                    e.target.value = 100;
                                }
                                if (e.target.value < 0) {
                                    e.target.value = 0;
                                }
                            }
                        }
                    }
                }
            }
        },
        {
            value: "Ventilazione",
            id: "VENTILATION",
            subcomponent: {
                ...subcomponentCashAccounting(t),
                labelWithTooltip: {
                    label: t("business.fields.cashAccounting.label"),
                    tooltip: {
                        title: t("business.fields.cashAccounting.tips.title"),
                        message: t("business.fields.cashAccounting.tips.description"),
                        placement: "bottom"
                    },
                    showTooltip: isEditing
                }
            }
        },
        {
            value: "Ventilazione macellai",
            id: "BUTCHERS_VENTILATION",
            subcomponent: {
                ...subcomponentCashAccounting(t),
                labelWithTooltip: {
                    label: t("business.fields.cashAccounting.label"),
                    tooltip: {
                        title: t("business.fields.cashAccounting.tips.title"),
                        message: t("business.fields.cashAccounting.tips.description"),
                        placement: "bottom"
                    },
                    showTooltip: isEditing
                }
            }
        },
        { value: "Agriturismo", id: "AGRITOURISM" },
        { value: "Associazione Sindacale", id: "TRADE_UNION_ASSOCIATION" },
        { value: "Attività connesse agricoltura", id: "RELATED_AGRICOLTURE_ACTIVITIES" },
        { value: "Enoturismo", id: "WINE_TOURISM" },
        { value: "Oleoturismo", id: "OLEOTOURISM" }
    ];
};

export const specialVatDeductionRate = {
    NO_SPECIAL_REGIME: 0,
    TRAVEL_AGENCY: 0,
    SPECIAL_SCHEME_OF_AGRICOLTURE: 0,
    AGRICOLTURE_EXEMPTION: 0,
    TRUCK_THIRD_PARTIES: 0,
    PUBLISHING: 0,
    ANALYTICAL_MARGIN: 0,
    GLOBAL_MARGIN: 0,
    FLAT_RATE_MARGIN: 0,
    VENTILATION: 0,
    BUTCHERS_VENTILATION: 0,
    AGRITOURISM: 50,
    TRADE_UNION_ASSOCIATION: 33,
    RELATED_AGRICOLTURE_ACTIVITIES: 50,
    WINE_TOURISM: 50,
    OLEOTOURISM: 50
};

export const vatPeriodicityOptions = t => {
    return [
        {
            id: "NORMAL_MONTHLY",
            value: t("business.fields.vatPeriodicity.NORMAL_MONTHLY")
        },
        {
            id: "MONTHLY_ADVANCE",
            value: t("business.fields.vatPeriodicity.MONTHLY_ADVANCE")
        },
        {
            id: "NORMAL_QUARTERLY",
            value: t("business.fields.vatPeriodicity.NORMAL_QUARTERLY")
        },
        {
            id: "QUARTERLY_ART_74_C_4",
            value: t("business.fields.vatPeriodicity.QUARTERLY_ART_74_C_4")
        }
    ];
};

export const vatExemptionManagementForExporterOptions = (t, bodyRequest, handleChange): RadioSubValues[] => [
    {
        id: "NO",
        label: t("general.no")
    },
    {
        id: "FIXED_PLAFOND",
        label: t("business.fields.plafondManagement.FIXED_PLAFOND")
    },
    {
        id: "MOBILE_PLAFOND",
        label: t("business.fields.plafondManagement.MOBILE_PLAFOND"),
        direction: "row",
        sublist: [
            {
                name: "vatExemptionForExporterStartingDate",
                component: "datepicker",
                fullWidth: true,
                label: t("business.fields.vatExemptionForExporterStartingDate"),
                value: bodyRequest?.vatExemptionForExporterStartingDate
                    ? getISOFromDateAndMonth(bodyRequest?.vatExemptionForExporterStartingDate)
                    : null,
                isEditing: true,
                onChange: val => {
                    handleChange(getISODateAndMonth(val), "vatExemptionForExporterStartingDate");
                },
                additionalProps: {
                    sx: { width: "40%" },
                    views: ["month", "year"],
                    maxDate: new Date(),
                    renderInput: params => (
                        <TextField
                            {...params}
                            inputProps={{
                                ...params.inputProps,
                                placeholder: "mm/aaaa"
                            }}
                        />
                    )
                }
            }
        ]
    }
];
